import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { realtimeDatabase } from '../../database/firebase';
import { Link, useLocation, useNavigate } from 'react-router-dom';
/**
 * Modal that shows all live users in the session (like allusers page).
 * Used in 2v2 lobby to invite people by sending them the room code.
 */
function InviteUsersModal({ sessionId, userId, inviterName, roomCode, onClose, theme }) {
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [invitingId, setInvitingId] = useState(null);
  const [pendingInviteUserIds, setPendingInviteUserIds] = useState([]); // userids we sent invite to (show Request, disabled, red)
  const listenerRef = useRef(null);
  const hostListenerRef = useRef(null);
  const [textcolor, Setcolorcode] = useState(null);
  const [userData, setUserData] = useState(null);
  const [themeData, setThemeData] = useState(null);

  useEffect(() => {

    const fetchData = async () => {
      const storedSession = sessionStorage.getItem("userData");
      console.log(storedSession);
      const storedTheme = sessionStorage.getItem("themeData");

      if (storedSession && storedTheme) {
        const parsedUser = JSON.parse(storedSession);
        const parsedTheme = JSON.parse(storedTheme);

        setUserData(parsedUser);
        setThemeData(parsedTheme);
        Setcolorcode(parsedTheme.landing_page_title_color);
      } else {
        navigate("/login"); // Redirect if session data is missing
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (!realtimeDatabase || !sessionId) {
      setLoading(false);
      return;
    }

    const sessionsRef = ref(realtimeDatabase, `sessions/${sessionId}/users`);
    listenerRef.current = onValue(sessionsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const arr = Object.keys(data).map((key) => ({
          userid: key,
          ...data[key],
        }));
        setUsers(arr);
      } else {
        setUsers([]);
      }
      setLoading(false);
    });

    return () => {
      if (listenerRef.current) listenerRef.current();
    };
  }, [sessionId]);

  // Listen for rejections so we can re-enable that user's button (Request -> Invite, red -> green)
  useEffect(() => {
    if (!realtimeDatabase || !sessionId || !userId) return;

    const hostRef = ref(realtimeDatabase, `sessions/${sessionId}/users/${userId}`);
    hostListenerRef.current = onValue(hostRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.val();
      const rejected = data.roomInviteRejected;
      if (rejected && rejected.by) {
        setPendingInviteUserIds((prev) => prev.filter((id) => id !== rejected.by));
      }
    });

    return () => {
      if (hostListenerRef.current) hostListenerRef.current();
    };
  }, [sessionId, userId]);

  const handleInvite = async (targetUser) => {
    if (!realtimeDatabase || !sessionId || invitingId) return;
    setInvitingId(targetUser.userid);
    try {
      await update(ref(realtimeDatabase, `sessions/${sessionId}/users/${targetUser.userid}`), {
        pendingRoomInvite: {
          roomCode,
          inviterName: inviterName || 'Someone',
          inviterId: userId,
          timestamp: Date.now(),
        },
      });
      setPendingInviteUserIds((prev) => (prev.includes(targetUser.userid) ? prev : [...prev, targetUser.userid]));
      // Keep modal open so host can invite more or see rejections; they close with Close button
    } catch (err) {
      console.error('Error sending invite:', err);
    } finally {
      setInvitingId(null);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#/game?gameId=${roomCode}`;
    const fallback = `${window.location.origin}/game?gameId=${roomCode}`;
    const toCopy = window.location.hash ? url : fallback;
    navigator.clipboard.writeText(toCopy).then(
      () => { },
      () => { navigator.clipboard.writeText(roomCode); }
    );
  };

  const filteredUsers = users.filter(
    (u) => u.userid !== userId && u.islive === true
  ).filter(
    (u) => !searchTerm.trim() || (u.name && u.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const boxStyle = theme?.box_color ? { background: theme.box_color, color: theme.box_text_color || '#1f2937' } : {};
  const btnStyle = theme?.button_color ? { background: theme.button_color, color: theme.button_text_color || '#fff' } : {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl min-w-[320px] max-h-[85vh] overflow-hidden border-4 border-amber-900 flex flex-col" style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-amber-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold" style={{ color: textcolor, "textAlign": "center","width": "100%",}}>Invite People</h2>
          <button type="button" onClick={onClose} className="text-2xl leading-none opacity-70 hover:opacity-100" title="Close">&times;</button>
        </div>

        <div className="p-4 flex flex-col flex-1 min-h-0">
          <div className="flex-shrink-0">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-base"
            />
          </div>

          {/* <div className="mb-2 flex-shrink-0 flex items-center justify-between gap-2">
            <span className="text-sm opacity-80">Room code: <strong className="font-mono">{roomCode}</strong></span>
            <button
              type="button"
              onClick={handleCopyLink}
              className="px-3 py-1.5 rounded-lg font-bold text-sm text-white bg-amber-600 hover:bg-amber-700"
            >
              Copy link
            </button>
          </div> */}

          {loading ? (
            <p className="text-center py-8">Loading users...</p>
          ) : (
            <div className="overflow-y-auto flex-1 min-h-[280px]" style={{ maxHeight: '55vh' }}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-amber-300">
                    <th className="py-3 pr-3 text-base">Name</th>
                    <th className="py-3 pr-3 text-base">Email</th>
                    <th className="py-3 w-28 text-base">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan={3} className="py-8 text-center opacity-70">No live users to invite</td></tr>
                  ) : (
                    filteredUsers.map((user) => {
                      const isPending = pendingInviteUserIds.includes(user.userid);
                      const isSending = invitingId === user.userid;
                      const isDisabled = isSending || isPending || user.status === 'playing' || user.status === 'pending';
                      const label = isSending ? 'Sending...' : isPending ? 'Request' : 'Invite';
                      return (
                        <tr key={user.userid} className="border-b border-amber-100">
                          <td className="py-3 pr-3 font-medium text-base">{user.name || user.firstName || '-'}</td>
                          <td className="py-3 pr-3 text-sm opacity-90">{user.email || '-'}</td>
                          <td className="py-3">
                            <button
                              type="button"
                              disabled={isDisabled}
                              onClick={() => handleInvite(user)}
                              className={`px-4 py-2 rounded-lg font-bold text-sm disabled:cursor-not-allowed text-white ${
                                isPending
                                  ? 'bg-red-600 opacity-90 cursor-not-allowed'
                                  : isSending
                                    ? 'bg-gray-500 opacity-70'
                                    : 'bg-green-600 hover:bg-green-700'
                              }`}
                              style={!isPending && !isSending ? btnStyle : undefined}
                            >
                              {label}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-amber-200 flex justify-end flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg font-bold text-white bg-amber-600 hover:bg-amber-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InviteUsersModal;
